import type { AgentOrchestratorStepResult } from '@axilon-apps/domains-ontology-ui-state'

/**
 * One inline SVG preview of a finished ingest job, rendered in the chat
 * turn below the orchestrator's final summary. Sourced from
 * `draw_cypher_subgraph` tool calls (and the legacy
 * `draw_ingest_subgraph` name in older chat history) inside an
 * `ingest_agent` step's `rawResponse.tool_calls` — each successful draw
 * call produces exactly one of these.
 *
 * The ingest agent renders one card per logical slice (Site & Gateway
 * overview, Tags under provider X, Routines in Program Main, ...), each
 * capped at 15 nodes, so a single ingest turn typically produces several
 * previews ordered as the agent surfaced them.
 */
export interface IngestSubgraphPreview {
  /** Stable per-tool-call id — used as the React key. */
  toolCallId: string
  /** Job id the SVG was rendered from, when the model passed it as input. */
  jobId: string | null
  /**
   * Friendly group caption — displayed as the card heading. Falls back
   * to "Ingest subgraph" when the agent didn't pass a title. Comes from
   * the tool output's `title` field (preferred) or its `label` field.
   */
  title: string
  /**
   * Primary node label the agent advertised as the focus of the slice
   * (e.g. "Tag", "Equipment"), when present in the tool output. Most
   * cypher-driven slices omit this and rely on `title` instead.
   */
  label: string | null
  /** Sanitized SVG markup as returned by `drawCypherSubgraphServer`. */
  svg: string
  nodeCount: number
  edgeCount: number
  /** Renderer note (e.g. "no nodes found"); shown below the diagram. */
  note: string | null
}

/**
 * Tool names that produce an inline ingest-subgraph card. The current
 * agent emits `draw_cypher_subgraph` for every diagram, but legacy
 * conversations created before the cypher-driven rewrite carry
 * `draw_ingest_subgraph` tool calls — keep both recognized so older
 * chat history still renders its diagrams.
 */
const SUBGRAPH_TOOL_NAMES = new Set<string>(['draw_cypher_subgraph', 'draw_ingest_subgraph'])

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function readJobId(input: unknown): string | null {
  const record = asRecord(input)
  if (record === null) return null
  const jobId = record.job_id ?? record.jobId
  return typeof jobId === 'string' && jobId.length > 0 ? jobId : null
}

function readNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

function readPreview(
  toolCallId: string,
  input: unknown,
  output: unknown
): IngestSubgraphPreview | null {
  const record = asRecord(output)
  if (record === null) return null
  const svg = typeof record.svg === 'string' ? record.svg : null
  if (svg === null || svg.length === 0) return null
  const nodeCount = typeof record.nodeCount === 'number' ? record.nodeCount : 0
  const edgeCount = typeof record.edgeCount === 'number' ? record.edgeCount : 0
  const note = readNonEmptyString(record.note)
  const inputRecord = asRecord(input)
  // Prefer the output's title (server canonicalizes friendly names from the
  // label) but fall back to whatever the agent passed in input or to the
  // bare label string so older traces still render with a useful caption.
  const title =
    readNonEmptyString(record.title) ??
    (inputRecord ? readNonEmptyString(inputRecord.title) : null) ??
    readNonEmptyString(record.label) ??
    (inputRecord ? readNonEmptyString(inputRecord.label) : null) ??
    'Ingest subgraph'
  const label =
    readNonEmptyString(record.label) ?? (inputRecord ? readNonEmptyString(inputRecord.label) : null)
  return {
    toolCallId,
    jobId: readJobId(input),
    title,
    label,
    svg,
    nodeCount,
    edgeCount,
    note
  }
}

/**
 * Walk every `ingest_agent` step result, pull each subgraph-rendering
 * tool call (current `draw_cypher_subgraph` and legacy
 * `draw_ingest_subgraph`), and project its
 * `{ svg, nodeCount, edgeCount, note }` output into the inline preview
 * shape used by `ChatTurn`. Order is preserved across steps + within
 * each step's tool-call list, so users see diagrams in the same
 * sequence the agent rendered them.
 */
export function aggregateStepIngestSubgraphs(
  stepResults: Iterable<AgentOrchestratorStepResult>
): IngestSubgraphPreview[] {
  const out: IngestSubgraphPreview[] = []
  for (const step of stepResults) {
    if (step.agent !== 'ingest_agent') continue
    if (!step.completed) continue
    const raw = asRecord(step.rawResponse)
    if (raw === null) continue
    const toolCalls = raw.tool_calls
    if (!Array.isArray(toolCalls)) continue
    for (const tc of toolCalls) {
      const tcRecord = asRecord(tc)
      if (tcRecord === null) continue
      if (typeof tcRecord.name !== 'string' || !SUBGRAPH_TOOL_NAMES.has(tcRecord.name)) {
        continue
      }
      const id = typeof tcRecord.id === 'string' ? tcRecord.id : null
      if (id === null) continue
      const preview = readPreview(id, tcRecord.input, tcRecord.output)
      if (preview !== null) out.push(preview)
    }
  }
  return out
}

export { sanitizeSubgraphSvg } from '../../lib/sanitize-svg'
